import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Music, Youtube, ExternalLink, X, Search, Loader2, ArrowLeft } from "lucide-react";
import { MediaLibrary } from "./MediaWidget";

// Abre Spotify en su propia ventana (Spotify sí requiere sesión externa).
const openPlatform = (url, name) =>
  window.open(url, name, "noopener,noreferrer,width=1200,height=840");

// Ventana emergente de YouTube DENTRO del sistema: buscar + ver videos embebidos.
// (youtube.com no se puede incrustar por su política X-Frame-Options; por eso usamos
//  búsqueda + reproductor embebido, todo sin salir de la app.)
function YouTubeModal({ onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(null); // {video_id, title, channel}
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Extrae el ID de un enlace de YouTube (watch, youtu.be, embed, shorts).
  const extractId = (val) => {
    const s = (val || "").trim();
    const m = s.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    if (m) return m[1];
    if (/^[\w-]{11}$/.test(s)) return s; // ID pegado directo
    return null;
  };

  const submit = async (term) => {
    const val = (term ?? q).trim();
    if (!val) return;
    const vid = extractId(val);
    if (vid) { setCurrent({ video_id: vid, title: "", channel: "" }); return; }
    // Si no es un enlace, intenta buscar por nombre (requiere API key configurada).
    setLoading(true); setResults(null);
    try {
      const { data } = await api.get("/media/youtube/search", { params: { q: val } });
      setResults(data.results || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo buscar en YouTube";
      toast.error(msg + " · Pega el enlace del video para verlo aquí.");
      setResults([]);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-stretch sm:items-center justify-center sm:p-4" data-testid="youtube-modal" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-5xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col h-full sm:h-[88vh]" onClick={(e) => e.stopPropagation()}>
        {/* Encabezado */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white shrink-0">
          <div className="flex items-center gap-2 font-display font-bold min-w-0">
            {current && (
              <button onClick={() => setCurrent(null)} data-testid="youtube-back" title="Volver" className="p-1.5 -ml-1.5 hover:bg-white/15 rounded-lg shrink-0"><ArrowLeft className="w-5 h-5" /></button>
            )}
            <Youtube className="w-5 h-5 shrink-0" /> <span className="truncate">YouTube</span>
          </div>
          <button onClick={onClose} data-testid="youtube-close" className="p-1.5 hover:bg-white/15 rounded-lg shrink-0"><X className="w-5 h-5" /></button>
        </div>

        {/* Reproductor */}
        {current && (
          <div className="bg-black shrink-0" data-testid="youtube-player">
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                key={current.video_id}
                title={current.title || "YouTube"}
                src={`https://www.youtube.com/embed/${current.video_id}?rel=0&autoplay=1`}
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture; accelerometer; gyroscope; web-share"
                allowFullScreen
              />
            </div>
            {current.title && <div className="px-4 py-2 text-sm text-white/90 truncate bg-black">{current.title}</div>}
          </div>
        )}

        {/* Barra: pegar enlace o buscar */}
        <div className="p-3 border-b border-nardo-100 shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              data-testid="youtube-search-input"
              placeholder="Pega el enlace de un video de YouTube para verlo aquí…"
              className="flex-1 min-w-0 border border-nardo-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-red-500"
            />
            <button onClick={() => submit()} disabled={loading} data-testid="youtube-search-btn" className="shrink-0 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-4 rounded-lg font-semibold disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Ver
            </button>
          </div>
          <p className="text-[11px] text-nardo-400 mt-1.5">Copia el enlace de cualquier video de YouTube (Compartir → Copiar enlace) y pégalo aquí para verlo dentro del sistema.</p>
        </div>

        {/* Resultados / estado */}
        <div className="flex-1 overflow-y-auto p-3" data-testid="youtube-results">
          {loading && <div className="py-16 text-center text-nardo-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}
          {!loading && results === null && !current && (
            <div className="py-16 text-center text-nardo-400 flex flex-col items-center gap-2">
              <Youtube className="w-10 h-10 text-red-400" />
              <p className="text-sm max-w-xs">Pega arriba el <b>enlace</b> de un video de YouTube y presiona <b>Ver</b> para reproducirlo aquí mismo.</p>
            </div>
          )}
          {!loading && results && results.length === 0 && (
            <div className="py-16 text-center text-sm text-nardo-400">Pega el enlace del video para verlo aquí.</div>
          )}
          {!loading && results && results.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {results.map((v) => (
                <button
                  key={v.video_id}
                  onClick={() => setCurrent(v)}
                  data-testid={`youtube-result-${v.video_id}`}
                  className={`text-left rounded-xl overflow-hidden border transition-all hover:shadow-lg hover:-translate-y-0.5 ${current?.video_id === v.video_id ? "border-red-500 ring-2 ring-red-200" : "border-nardo-200"}`}
                >
                  <div className="relative">
                    <img src={v.thumb} alt="" className="w-full aspect-video object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/40 transition-opacity">
                      <Youtube className="w-10 h-10 text-white" />
                    </span>
                  </div>
                  <div className="p-2.5">
                    <div className="text-sm font-medium text-navy line-clamp-2 leading-snug">{v.title}</div>
                    <div className="text-[11px] text-nardo-400 mt-1 truncate">{v.channel}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MusicModule() {
  const [ytOpen, setYtOpen] = useState(false);

  return (
    <div className="space-y-6" data-testid="music-module">
      {/* Dos ventanas: Spotify y YouTube */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          onClick={() => openPlatform("https://open.spotify.com", "uaicard_spotify")}
          data-testid="music-open-spotify"
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-6 text-left shadow-lg shadow-emerald-900/20 hover:scale-[1.02] active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center"><Music className="w-6 h-6" /></div>
            <div>
              <div className="font-display font-bold text-xl flex items-center gap-2">Spotify <ExternalLink className="w-4 h-4 opacity-80" /></div>
              <div className="text-sm text-white/80">Abre la plataforma de Spotify en una ventana</div>
            </div>
          </div>
          <span className="mt-4 inline-block text-xs font-semibold uppercase tracking-wider bg-white/20 rounded-full px-3 py-1">Abrir Spotify →</span>
        </button>

        <button
          onClick={() => setYtOpen(true)}
          data-testid="music-open-youtube"
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-500 to-red-700 text-white p-6 text-left shadow-lg shadow-red-900/20 hover:scale-[1.02] active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center"><Youtube className="w-6 h-6" /></div>
            <div>
              <div className="font-display font-bold text-xl flex items-center gap-2">YouTube</div>
              <div className="text-sm text-white/80">Busca y mira videos aquí mismo, sin salir del sistema</div>
            </div>
          </div>
          <span className="mt-4 inline-block text-xs font-semibold uppercase tracking-wider bg-white/20 rounded-full px-3 py-1">Abrir YouTube →</span>
        </button>
      </div>

      {/* Biblioteca embebida: reproductor, buscador y enlaces guardados/compartidos */}
      <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-navy text-white font-semibold text-sm">
          <Music className="w-4 h-4" /> Tu biblioteca de música y videos
        </div>
        <MediaLibrary variant="page" />
      </div>

      {ytOpen && <YouTubeModal onClose={() => setYtOpen(false)} />}
    </div>
  );
}
